// Inbox: fällige Aufgaben und Follow-ups (bis Ende heute bzw. ohne Datum), geplante, erledigte.
// Reiter Alle (Primary), Calls (verpasst, Voicemail, angenommen ohne Lead, mit Rückruf) und Aufgaben (Follow-ups und
// Anrufaufgaben), Personenwahl, Auswahlmodus mit Erledigen und Verschieben für viele. Erledigen, verschieben, Lead öffnen;
// j/k, Enter, x (markieren), e/y (erledigt). Echtzeit hält die Liste aktuell.
// Seit Runde 6 (6D) zwei weitere Reiter, die nicht aus Aufgaben bestehen: Emails (Mail-Eingang, `components/inbox/MailReiter.tsx`)
// und Messages (SMS, `components/inbox/SmsReiter.tsx`). Sie bringen ihre eigene Leiste, Liste und Tasten mit; die Zahlen im
// Reiterkopf kommen aus `GET /api/mail-eingang/zaehler` und `GET /api/sms/zaehler`.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Aufgabe, InboxAnsicht } from "../api";
import { inboxApi, type InboxKategorie, type InboxReiter, type ReiterZaehler } from "../api/inbox";
import { useEchtzeit } from "../api/echtzeit";
import { mailEingangApi, type MailZaehler } from "../api/mailEingang";
import { smsEingangApi, type SmsZaehler } from "../api/smsEingang";
import { AufgabeZeile, prioritaetRang, useAufgabenAktionen } from "../components/Aufgaben";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon, type IconName } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { MailReiter } from "../components/inbox/MailReiter";
import { NummerPanel } from "../components/inbox/NummerPanel";
import { PersonenWahl, useDarfFremde } from "../components/inbox/PersonenWahl";
import { SmsReiter } from "../components/inbox/SmsReiter";
import { VerschiebenPopover } from "../components/inbox/Verschieben";
import { hatNummer, istAnrufbar, istSystem } from "../components/inbox/typen";
import { listeLesen, listeSetzen } from "../daten/liste";
import { useSeitenKopf } from "../daten/seite";
import { AnrufenKnopf } from "../telefonie";
import { useNeuVerbunden } from "../daten/verbindung";
import { T } from "../texte";
import { useTasten } from "../werkzeuge/tasten";
import { berlinDatum, faelligkeit, heute, tagText } from "../werkzeuge/zeit";

const ANSICHTEN: { wert: InboxAnsicht; label: string }[] = [
  { wert: "inbox", label: T.inbox.faellig },
  { wert: "future", label: T.inbox.geplant },
  { wert: "done", label: T.inbox.erledigt },
];

const REITER: { wert: InboxReiter; label: string; icon: IconName; titel: string }[] = [
  { wert: "alle", label: "Alle", icon: "inbox", titel: "Alles, was fällig ist" },
  { wert: "calls", label: "Calls", icon: "telefon", titel: "Verpasste Anrufe, Voicemails, angenommene Anrufe ohne Lead" },
  { wert: "aufgaben", label: "Aufgaben", icon: "aufgabe", titel: "Follow-ups und Anrufaufgaben" },
  { wert: "emails", label: "Emails", icon: "mail", titel: "Eingegangene Mails, die einem Lead zugeordnet werden müssen oder noch ungelesen sind" },
  { wert: "messages", label: "Messages", icon: "sms", titel: "Eingegangene und gesendete SMS, Antworten, Fehler und geplante SMS" },
];

function gruppeVon(a: Aufgabe, ansicht: InboxAnsicht): string {
  if (ansicht === "done") return "";
  if (!a.date && !a.due_at) return "Ohne Datum";
  const f = faelligkeit(a.date, a.due_at);
  if (ansicht === "inbox") return f.ueberfaellig && !f.heute ? "Überfällig" : "Heute";
  const tag = a.date && a.date.length > 10 ? berlinDatum(a.date) : (a.date ?? berlinDatum(a.due_at ?? new Date().toISOString())).slice(0, 10);
  return tag === heute() ? "Heute" : tagText(tag);
}

export default function Inbox() {
  useSeitenKopf([{ label: "Inbox" }]);
  const navigate = useNavigate();
  const meldung = useMeldung();
  const darfFremde = useDarfFremde();
  const [params, setParams] = useSearchParams();
  const ansicht = (["inbox", "future", "done"].includes(params.get("ansicht") ?? "") ? params.get("ansicht") : "inbox") as InboxAnsicht;
  const reiter = (["alle", "calls", "aufgaben", "emails", "messages"].includes(params.get("reiter") ?? "") ? params.get("reiter") : "alle") as InboxReiter;
  const nachrichten = reiter === "emails" || reiter === "messages"; // eigene Listen (6D), keine Aufgaben
  const kategorie: InboxKategorie = nachrichten ? "alle" : (reiter as InboxKategorie);
  const wer = darfFremde ? params.get("wer") || "me" : "me";
  const [aufgaben, setAufgaben] = useState<Aufgabe[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const [fokus, setFokus] = useState(0);
  const [zaehler, setZaehler] = useState<ReiterZaehler | null>(null);
  const [smsZaehler, setSmsZaehler] = useState<SmsZaehler | null>(null);
  const [mailZaehler, setMailZaehler] = useState<MailZaehler | null>(null);
  const [nummerOffen, setNummerOffen] = useState<string | null>(null); // Aufgabe ohne Lead: Nummern-Suche aufgeklappt
  const [markiert, setMarkiert] = useState<Set<string>>(new Set());
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [verschiebenOffen, setVerschiebenOffen] = useState(false);
  const verschiebenKnopf = useRef<HTMLButtonElement>(null);
  const letzterKlick = useRef<number | null>(null);
  const generation = useRef(0);
  const aktionen = useAufgabenAktionen();

  /** Anderer Reiter: Filter und Suche der Nachrichten-Reiter gelten dort nicht mehr */
  const reiterWaehlen = (wert: InboxReiter) => {
    const neu = new URLSearchParams(params);
    if (wert === "alle") neu.delete("reiter");
    else neu.set("reiter", wert);
    neu.delete("filter");
    neu.delete("q");
    setParams(neu, { replace: true });
  };

  const setParam = (k: string, v: string | null) => {
    const neu = new URLSearchParams(params);
    if (v) neu.set(k, v);
    else neu.delete(k);
    setParams(neu, { replace: true });
  };

  // Ein anderer Reiter, eine andere Ansicht oder Person: die Markierung gilt nicht mehr
  useEffect(() => {
    setMarkiert(new Set());
    letzterKlick.current = null;
  }, [ansicht, reiter, wer]);

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (nachrichten) {
        setLaedt(false); // Emails und Messages laden ihre Listen selbst
        return;
      }
      if (!still) setLaedt(true);
      setFehler(null);
      try {
        const [r, z] = await Promise.all([inboxApi.liste({ view: ansicht, assigned_to: wer, kategorie, limit: 200 }), inboxApi.reiter(wer).catch(() => null)]);
        if (gen !== generation.current) return;
        setAufgaben(ordnen(r.data, ansicht));
        setCursor(r.has_more ? r.cursor : null);
        setZaehler(z);
        // Markierte, die nicht mehr in der Liste stehen (anderswo erledigt), fallen heraus
        setMarkiert((m) => {
          const da = new Set(r.data.map((a) => a.id));
          const rest = [...m].filter((id) => da.has(id));
          return rest.length === m.size ? m : new Set(rest);
        });
        if (!still) {
          const k = listeLesen();
          const i = k?.quelle === "inbox" && k.fokus ? r.data.findIndex((a) => a.lead_id === k.fokus) : -1;
          setFokus(i >= 0 ? i : 0);
        }
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    [ansicht, wer, kategorie, nachrichten],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  // Zahlen der Reiter Messages und Emails (jede Person sieht ihre eigenen; bei fremder Ansicht bleiben sie die eigenen)
  const nachrichtenZaehlerLaden = useCallback(() => {
    smsEingangApi.zaehler().then(setSmsZaehler).catch(() => undefined);
    mailEingangApi.zaehler().then(setMailZaehler).catch(() => undefined);
  }, []);
  useEffect(() => nachrichtenZaehlerLaden(), [nachrichtenZaehlerLaden]);
  useEchtzeit(["sms.received", "sms.updated", "mail.received", "mail.updated"], () => nachrichtenZaehlerLaden());

  const zeit = useRef<number | undefined>(undefined);
  useEchtzeit(["task.created", "task.updated"], () => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => void laden(true), 400);
  });
  useNeuVerbunden(() => void laden(true));
  useEffect(() => () => window.clearTimeout(zeit.current), []);

  const mehr = async () => {
    if (!cursor) return;
    try {
      const r = await inboxApi.liste({ view: ansicht, assigned_to: wer, kategorie, limit: 200, cursor });
      setAufgaben((a) => [...a, ...r.data.filter((x) => !a.some((y) => y.id === x.id))]);
      setCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      setFehler(e);
    }
  };

  const leadIds = useMemo(() => [...new Set(aufgaben.map((a) => a.lead_id).filter((x): x is string => !!x))], [aufgaben]);

  const leadOeffnen = (leadId: string) => {
    listeSetzen({ quelle: "inbox", titel: "Inbox", pfad: `/inbox${params.toString() ? `?${params.toString()}` : ""}`, ids: leadIds.includes(leadId) ? leadIds : [...leadIds, leadId], fokus: leadId });
    navigate(`/lead/${encodeURIComponent(leadId)}`);
  };

  const oeffnen = (a: Aufgabe) => {
    if (!a.lead_id) {
      // Anruf ohne Lead: Suche nach der Nummer unter der Zeile auf- und zuklappen
      if (hatNummer(a)) setNummerOffen((o) => (o === a.id ? null : a.id));
      return;
    }
    leadOeffnen(a.lead_id);
  };

  const zaehlerNeu = useCallback(() => {
    inboxApi.reiter(wer).then(setZaehler).catch(() => undefined);
  }, [wer]);

  const uebernehmen = useCallback(
    (neu: Aufgabe) => {
      setAufgaben((liste) => {
        const raus = (ansicht === "inbox" || ansicht === "future") && (neu.is_complete || (ansicht === "inbox" && !faelligBisHeute(neu)) || (ansicht === "future" && faelligBisHeute(neu)));
        const raus2 = ansicht === "done" && !neu.is_complete;
        return raus || raus2 ? liste.filter((a) => a.id !== neu.id) : liste.map((a) => (a.id === neu.id ? neu : a));
      });
      setMarkiert((m) => {
        if (!m.has(neu.id)) return m;
        const rest = new Set(m);
        rest.delete(neu.id);
        return rest;
      });
    },
    [ansicht],
  );

  const geaendert = (neu: Aufgabe) => {
    uebernehmen(neu);
    zaehlerNeu();
  };

  // ---- Auswahlmodus -------------------------------------------------------------------------------------------
  const umschalten = (i: number, bereich: boolean) => {
    const neu = new Set(markiert);
    const an = !neu.has(aufgaben[i].id);
    const von = bereich && letzterKlick.current !== null && letzterKlick.current < aufgaben.length ? Math.min(letzterKlick.current, i) : i;
    const bis = bereich && letzterKlick.current !== null && letzterKlick.current < aufgaben.length ? Math.max(letzterKlick.current, i) : i;
    for (let k = von; k <= bis; k++) (an ? neu.add(aufgaben[k].id) : neu.delete(aufgaben[k].id));
    letzterKlick.current = i;
    setMarkiert(neu);
  };
  const alleAn = aufgaben.length > 0 && aufgaben.every((a) => markiert.has(a.id));
  const einigeAn = !alleAn && markiert.size > 0;
  const alleUmschalten = () => {
    letzterKlick.current = null;
    setMarkiert(alleAn ? new Set() : new Set(aufgaben.map((a) => a.id)));
  };

  /** Mehrere auf einmal (ein Aufruf, jede Aufgabe geht einzeln nach Close; Fehler stoppen die anderen nicht) */
  const sammeln = async (ids: string[], aenderung: { is_complete?: boolean; date?: string | null }, wort: string) => {
    if (!ids.length || beschaeftigt) return;
    setBeschaeftigt(true);
    try {
      const r = await inboxApi.mehrere(ids, aenderung);
      for (const e of r.ergebnisse) if (e.ok && e.task) uebernehmen(e.task);
      zaehlerNeu();
      const schlecht = r.ergebnisse.filter((e) => !e.ok);
      if (r.ok) meldung.ok(`${r.ok} ${r.ok === 1 ? "Eintrag" : "Einträge"} ${wort}.`);
      if (schlecht.length) {
        meldung.zeige("fehler", `${schlecht.length} nicht gespeichert: ${schlecht[0].fehler?.message ?? "Fehler"}`);
        setMarkiert(new Set(schlecht.map((e) => e.id)));
      }
    } catch (e) {
      meldung.fehler(e, "Nicht gespeichert");
    } finally {
      setBeschaeftigt(false);
    }
  };

  const erledigenMarkierte = () => void sammeln([...markiert], { is_complete: true }, "erledigt");
  const verschiebenMarkierte = (date: string) => {
    const ziel = aufgaben.filter((a) => markiert.has(a.id));
    const system = ziel.filter(istSystem);
    if (system.length) meldung.zeige("info", `${system.length} ${system.length === 1 ? "Eintrag" : "Einträge"} (Anrufe, SMS, Opportunity) lassen sich nur erledigen und bleiben.`);
    void sammeln(ziel.filter((a) => !istSystem(a)).map((a) => a.id), { date }, "verschoben");
  };

  const erledigt = () => {
    if (markiert.size) return erledigenMarkierte();
    const a = aufgaben[fokus];
    if (a && !a.is_complete) void aktionen.erledigen(a).then((neu) => neu && geaendert(neu));
  };

  useTasten({
    j: () => setFokus((f) => Math.min(f + 1, aufgaben.length - 1)),
    ArrowDown: () => setFokus((f) => Math.min(f + 1, aufgaben.length - 1)),
    k: () => setFokus((f) => Math.max(f - 1, 0)),
    ArrowUp: () => setFokus((f) => Math.max(f - 1, 0)),
    Enter: () => {
      const a = aufgaben[fokus];
      if (a) oeffnen(a);
    },
    "mod+Enter": () => {
      const a = aufgaben[fokus];
      if (a?.lead_id) window.open(`/lead/${encodeURIComponent(a.lead_id)}`, "_blank", "noopener");
    },
    x: () => {
      if (aufgaben[fokus]) umschalten(fokus, false);
    },
    e: erledigt,
    y: erledigt,
    Escape: () => {
      if (markiert.size) setMarkiert(new Set());
    },
  }, !nachrichten);

  useEffect(() => {
    document.querySelector(`[data-aufgabe="${aufgaben[fokus]?.id}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus, aufgaben]);

  const zahl = (r: InboxReiter): number | null => {
    if (r === "messages") return smsZaehler ? smsZaehler.ungelesen : null;
    if (r === "emails") return mailZaehler ? mailZaehler.ungelesen : null;
    if (!zaehler || ansicht !== "inbox") return null;
    return r === "calls" ? zaehler.calls : r === "aufgaben" ? zaehler.aufgaben : zaehler.inbox;
  };

  const leerTitel =
    ansicht === "inbox" ? "Alles erledigt." : ansicht === "future" ? "Nichts geplant." : "Noch nichts erledigt.";
  const leerText =
    ansicht !== "inbox" ? undefined : reiter === "calls" ? "Keine offenen Anrufe oder Voicemails." : reiter === "aufgaben" ? "Keine fälligen Aufgaben." : "Keine fälligen Aufgaben oder Follow-ups.";

  let letzte = "__";
  return (
    <div className="liste-seite">
      <div className="liste-kopf">
        <h1 className="liste-kopf__titel">Inbox</h1>
        {nachrichten ? null : <div className="reiter reiter--seite" role="tablist" aria-label="Ansicht">
          {ANSICHTEN.map((a) => (
            <button key={a.wert} type="button" role="tab" aria-selected={ansicht === a.wert} className={`reiter__knopf${ansicht === a.wert ? " reiter__knopf--an" : ""}`} onClick={() => setParam("ansicht", a.wert === "inbox" ? null : a.wert)}>
              {a.label}
              {a.wert === "inbox" && zaehler && wer === "me" ? <span className="reiter__zahl">{zaehler.inbox}</span> : null}
              {a.wert === "future" && zaehler && wer === "me" ? <span className="reiter__zahl reiter__zahl--leise">{zaehler.future}</span> : null}
            </button>
          ))}
        </div>}
        <div className="liste-kopf__rechts">
          <AnrufenKnopf />
        </div>
      </div>
      <div className="inbox-reiter">
        <div className="reiter" role="tablist" aria-label="Art der Einträge">
          {REITER.map((r) => {
            const n = zahl(r.wert);
            return (
              <button key={r.wert} type="button" role="tab" aria-selected={reiter === r.wert} title={r.titel} className={`reiter__knopf${reiter === r.wert ? " reiter__knopf--an" : ""}`} onClick={() => reiterWaehlen(r.wert)}>
                <Icon name={r.icon} groesse={14} />
                {r.label}
                {n !== null && (n > 0 || (r.wert !== "emails" && r.wert !== "messages")) ? <span className="reiter__anzahl">{n}</span> : null}
                {r.wert === "messages" && smsZaehler && smsZaehler.fehlgeschlagen > 0 ? (
                  <span className="reiter__anzahl reiter__anzahl--fehler" title={`${smsZaehler.fehlgeschlagen} SMS nicht angekommen`}>
                    {smsZaehler.fehlgeschlagen}!
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        {darfFremde ? <PersonenWahl wer={wer} onWahl={(w) => setParam("wer", w === "me" ? null : w)} /> : null}
      </div>
      {reiter === "messages" ? (
        <SmsReiter wer={wer} zaehler={smsZaehler} onZaehler={nachrichtenZaehlerLaden} />
      ) : reiter === "emails" ? (
        <MailReiter wer={wer} zaehler={mailZaehler} onZaehler={nachrichtenZaehlerLaden} />
      ) : (
        <>
      <div className={`liste-leiste inbox-leiste${markiert.size ? " inbox-leiste--auswahl" : ""}`}>
        <input
          type="checkbox"
          className="inbox-leiste__alle"
          checked={alleAn}
          ref={(el) => {
            if (el) el.indeterminate = einigeAn;
          }}
          onChange={alleUmschalten}
          disabled={!aufgaben.length}
          title="Alle geladenen Einträge markieren"
          aria-label="Alle geladenen Einträge markieren"
        />
        {markiert.size ? (
          <>
            <span className="liste-leiste__zahl">{markiert.size} markiert</span>
            <button type="button" className="knopf knopf--klein" disabled={beschaeftigt} onClick={erledigenMarkierte} title="Markierte erledigen (e oder y)">
              <Icon name="haken" groesse={13} /> Erledigt
            </button>
            <button ref={verschiebenKnopf} type="button" className="knopf knopf--klein" disabled={beschaeftigt || ansicht === "done"} onClick={() => setVerschiebenOffen((o) => !o)} title="Markierte verschieben">
              <Icon name="uhr" groesse={13} /> Verschieben
            </button>
            <VerschiebenPopover anker={verschiebenKnopf.current} offen={verschiebenOffen} ausrichtung="start" onSchliessen={() => setVerschiebenOffen(false)} onWahl={verschiebenMarkierte} hinweis={`Gilt für ${markiert.size} ${markiert.size === 1 ? "Eintrag" : "Einträge"}.`} />
            <button type="button" className="knopf knopf--klein knopf--leise" onClick={() => setMarkiert(new Set())} title="Auswahl aufheben (Esc)">
              Auswahl aufheben
            </button>
          </>
        ) : (
          <>
            <span className="liste-leiste__zahl">
              {aufgaben.length} {aufgaben.length === 1 ? "Eintrag" : "Einträge"}
              {zaehler && zaehler.overdue > 0 && ansicht === "inbox" && wer === "me" ? <span className="text-gefahr"> · {zaehler.overdue} überfällig</span> : null}
            </span>
            <span className="liste-leiste__tasten leise">
              <kbd>x</kbd> markieren · <kbd>e</kbd> erledigt · <kbd>Enter</kbd> Lead öffnen oder Nummer suchen
            </span>
          </>
        )}
      </div>
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} /> : null}
      {laedt && !aufgaben.length ? (
        <Laden />
      ) : !fehler && !aufgaben.length ? (
        <Leer titel={leerTitel} text={leerText} />
      ) : (
        <div className={`aufgaben-liste${wer !== "me" ? " aufgaben-liste--personen" : ""}${aufgaben.some(istAnrufbar) ? " aufgaben-liste--anrufe" : ""}`}>
          {aufgaben.map((a, i) => {
            const g = gruppeVon(a, ansicht);
            const kopf = g !== letzte && g ? g : null;
            letzte = g;
            return (
              <div key={a.id}>
                {kopf ? <div className={`aufgaben-gruppe${kopf === "Überfällig" ? " aufgaben-gruppe--gefahr" : ""}`}>{kopf}</div> : null}
                <AufgabeZeile
                  aufgabe={a}
                  mitLead
                  fokus={i === fokus}
                  onGeaendert={geaendert}
                  onOeffnen={() => (setFokus(i), oeffnen(a))}
                  auswahl={{ an: markiert.has(a.id), onUmschalten: (bereich) => (setFokus(i), umschalten(i, bereich)) }}
                  onRueckruf={!a.lead_id && hatNummer(a) ? () => setNummerOffen((o) => (o === a.id ? null : a.id)) : undefined}
                />
                {nummerOffen === a.id && !a.lead_id ? <NummerPanel aufgabe={a} onLead={leadOeffnen} onSchliessen={() => setNummerOffen(null)} /> : null}
              </div>
            );
          })}
          {cursor ? (
            <div className="tabelle__mehr">
              <button type="button" className="knopf" onClick={() => void mehr()}>
                Weitere laden
              </button>
            </div>
          ) : null}
        </div>
      )}
        </>
      )}
    </div>
  );
}

/** Inbox: erst überfällig, dann heute, dann ohne Datum; je Gruppe Priorität Hoch zuerst, Niedrig zuletzt, dann nach
 * Fälligkeit (älteste zuerst). */
function ordnen(liste: Aufgabe[], ansicht: InboxAnsicht): Aufgabe[] {
  if (ansicht !== "inbox") return liste;
  const rang = (a: Aufgabe) => {
    const g = gruppeVon(a, ansicht);
    return g === "Überfällig" ? 0 : g === "Heute" ? 1 : 2;
  };
  return [...liste].sort((a, b) => rang(a) - rang(b) || prioritaetRang(a) - prioritaetRang(b) || (a.due_at ?? "").localeCompare(b.due_at ?? ""));
}

function faelligBisHeute(a: Aufgabe): boolean {
  if (!a.date && !a.due_at) return true;
  const f = faelligkeit(a.date, a.due_at);
  return f.heute || f.ueberfaellig;
}
